/**
 * Search — query/facets/folders/tags inputs, relevance-vs-latest sort,
 * highlighted result snippets, and tag and folder cloud tabs with live
 * filtering. The two clouds are one component over two lists; picking a chip
 * in either writes into the matching form box and re-runs the search.
 *
 * The current search lives in the URL so results are linkable and the back
 * button behaves; in-page updates use replaceState so the tag cloud keeps its
 * own filter text while the results refresh behind it.
 */

import { searchNotes, fetchTags, fetchFolderCounts } from "../api/notes";
import type { FolderCount, SearchQuery, SearchResponse, SearchSort, TagCount } from "../api/types";
import { noteRow } from "../components/noteList";
import { pageHeader } from "../components/pageHeader";
import { searchForm } from "../components/searchForm";
import type { SearchFormValue } from "../components/searchForm";
import { parseTagInput } from "../lib/tagInput";
import { emptyState } from "../components/states";
import { chipCloud } from "../components/chipCloud";
import type { ChipCloudHandle } from "../components/chipCloud";
import { runAsyncView } from "../lib/asyncView";
import { el } from "../lib/dom";
import { renderSnippet } from "../lib/highlight";
import { searchPath } from "../router/paths";
import type { PageContext, PageView } from "../router/router";

const RESULT_LIMIT = 50;

type Tab = "results" | "tags" | "folders";

function readSort(raw: string | null): SearchSort {
  return raw === "latest" ? "latest" : "relevance";
}

function readTab(raw: string | null): Tab {
  return raw === "tags" || raw === "folders" ? raw : "results";
}

function readInitialValue(context: PageContext): SearchFormValue {
  return {
    query: context.query.get("query") ?? "",
    facets: context.query.get("facets") ?? "",
    tags: parseTagInput(context.query.get("tags") ?? ""),
    folders: parseTagInput(context.query.get("folders") ?? ""),
    sort: readSort(context.query.get("sort")),
  };
}

function hasCriteria(value: SearchFormValue): boolean {
  return value.query !== "" || value.facets !== "" || value.tags.length > 0 || value.folders.length > 0;
}

function toQuery(value: SearchFormValue): SearchQuery {
  return { ...value, limit: RESULT_LIMIT };
}

function resultsView(response: SearchResponse): Node {
  if (response.hits.length === 0) {
    return emptyState("No matches", "Try fewer tags or a broader query.");
  }

  return el("div", {
    className: "results",
    children: [
      el("p", {
        className: "results__count",
        text: `${response.total} ${response.total === 1 ? "match" : "matches"}`,
      }),
      el("ul", {
        className: "note-list",
        children: response.hits.map((hit) =>
          el("li", {
            children: [
              noteRow({
                note: hit.note,
                ...(hit.snippet === "" ? {} : { snippet: renderSnippet(hit.snippet) }),
              }),
            ],
          }),
        ),
      }),
    ],
  });
}

export function searchPage(context: PageContext): PageView {
  let value: SearchFormValue = readInitialValue(context);
  let tab: Tab = readTab(context.query.get("tab"));
  let tagCloudHandle: ChipCloudHandle | null = null;
  let folderCloudHandle: ChipCloudHandle | null = null;

  const resultsPanel = el("div", { className: "panel", attrs: { id: "search-results-panel" } });
  const tagsPanel = el("div", { className: "panel", attrs: { id: "search-tags-panel" } });
  const foldersPanel = el("div", { className: "panel", attrs: { id: "search-folders-panel" } });

  const syncUrl = (): void => {
    const target = searchPath({ ...value, tab });
    window.history.replaceState(null, "", target);
  };

  // The Results tab carries the match count once a search has run, so the
  // number is visible even while the Tags tab is showing. The button is
  // created further down; by the time a response arrives it exists.
  let resultsTab: HTMLButtonElement | null = null;
  const setResultsLabel = (total: number | null): void => {
    if (resultsTab !== null) {
      resultsTab.textContent = total === null ? "Results" : `Results (${total})`;
    }
  };

  const results = runAsyncView<SearchResponse | null>({
    container: resultsPanel,
    loadingLabel: "Searching…",
    load: async (signal) => (hasCriteria(value) ? searchNotes(toQuery(value), signal) : null),
    render: (response) => {
      setResultsLabel(response === null ? null : response.total);
      return response === null
        ? emptyState("Search your notes", "Enter a query, filter by facets, or pick folders and tags.")
        : resultsView(response);
    },
  });

  const applyValue = (next: SearchFormValue): void => {
    value = next;
    syncUrl();
    results.reload();
  };

  const form = searchForm({ initial: value, onSubmit: applyValue });

  const toggleTag = (tag: string): void => {
    const next = value.tags.includes(tag) ? value.tags.filter((entry) => entry !== tag) : [...value.tags, tag];
    form.setTags(next);
    tagCloudHandle?.setSelected(next);
    applyValue({ ...value, tags: next });
  };

  const toggleFolder = (folder: string): void => {
    const next = value.folders.includes(folder)
      ? value.folders.filter((entry) => entry !== folder)
      : [...value.folders, folder];
    form.setFolders(next);
    folderCloudHandle?.setSelected(next);
    applyValue({ ...value, folders: next });
  };

  const tags = runAsyncView<readonly TagCount[]>({
    container: tagsPanel,
    loadingLabel: "Loading tags…",
    load: (signal) => fetchTags(signal),
    render: (allTags) => {
      if (allTags.length === 0) {
        return emptyState("No tags yet", "Tags appear here once notes carry them.");
      }
      const handle = chipCloud({
        items: allTags.map((entry) => ({ label: entry.tag, count: entry.count })),
        selected: value.tags,
        noun: "tags",
        onToggle: toggleTag,
      });
      tagCloudHandle = handle;
      return handle.element;
    },
  });

  // Fetched fresh each time the page opens, so a folder created a moment ago
  // in the editor is already offered here.
  const folders = runAsyncView<readonly FolderCount[]>({
    container: foldersPanel,
    loadingLabel: "Loading folders…",
    load: (signal) => fetchFolderCounts(signal),
    render: (allFolders) => {
      if (allFolders.length === 0) {
        return emptyState("No folders yet", "Folders appear here once there are notes.");
      }
      const handle = chipCloud({
        items: allFolders.map((entry) => ({ label: entry.folder, count: entry.count })),
        selected: value.folders,
        noun: "folders",
        onToggle: toggleFolder,
      });
      folderCloudHandle = handle;
      return handle.element;
    },
  });

  const tabButtons: readonly { readonly id: Tab; readonly label: string; readonly button: HTMLButtonElement }[] = (
    [
      { id: "results", label: "Results" },
      { id: "folders", label: "Folders" },
      { id: "tags", label: "Tags" },
    ] as const
  ).map((entry) => ({
    ...entry,
    button: el("button", {
      className: "tabs__tab",
      attrs: { type: "button", role: "tab", "aria-controls": `search-${entry.id}-panel` },
      text: entry.label,
    }),
  }));

  const applyTab = (next: Tab): void => {
    tab = next;
    for (const entry of tabButtons) {
      const active = entry.id === next;
      entry.button.classList.toggle("tabs__tab--active", active);
      entry.button.setAttribute("aria-selected", String(active));
    }
    resultsPanel.hidden = next !== "results";
    tagsPanel.hidden = next !== "tags";
    foldersPanel.hidden = next !== "folders";
    syncUrl();
  };

  for (const entry of tabButtons) {
    entry.button.addEventListener("click", () => {
      applyTab(entry.id);
    });
  }

  resultsTab = tabButtons.find((entry) => entry.id === "results")?.button ?? null;

  const tabsBar = el("div", {
    className: "tabs",
    attrs: { role: "tablist", "aria-label": "Search views" },
    children: tabButtons.map((entry) => entry.button),
  });

  applyTab(tab);

  const element = el("section", {
    className: "page page--search",
    children: [
      pageHeader({ title: "Search", subtitle: "Full-text, facets, folders and tags across every note." }),
      form.element,
      tabsBar,
      resultsPanel,
      foldersPanel,
      tagsPanel,
    ],
  });

  return {
    element,
    dispose(): void {
      results.dispose();
      tags.dispose();
      folders.dispose();
    },
  };
}
